// Results (spec §2): analytics over a segment — the sticky segment bar, then
// Summary, Trend, one card per step and Time of day, with a nav down the side.
// The segment lives in the URL in kore's grammar, with the same keys as
// Responses.

import { Badge, Button, buttonVariants, SectionNav, type SectionNavItem, useKoreSearch } from "@pej/kore/components";
import { Link } from "@tanstack/react-router";
import { type CSSProperties, useMemo, useState } from "react";
import { usePlacements } from "@/api/placements";
import { useStats } from "@/api/stats";
import type { PlacementRow, Survey } from "@/api/types";
import { PageError, PageLoading, useRetry } from "@/components/page-states";
import { SectionCard } from "@/components/section-page/section-card";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useElementHeight } from "@/components/section-page/use-element-height";
import { HeatmapCard } from "@/features/dashboard/heatmap-card";
import { StepSummaryCard } from "@/features/dashboard/step-summary-card";
import { SummaryTiles } from "@/features/dashboard/summary-tiles";
import { TrendChart } from "@/features/dashboard/trend-chart";
import { useUrlSearch } from "@/features/search/page-search";
import { stepPrompt } from "@/features/surveys/steps/step-list";
import { presetOf, presetPatch, useTimeChipRouter } from "@/features/time/time-chip";
import {
    filtersForWire,
    type RawFilters,
    RESPONSE_DEFAULT_ORDER,
    segmentManifest,
    segmentSearch,
} from "./responses-filters";
import { RESULTS_SECTION } from "./sections";
import { SegmentBar } from "./segment-bar";
import { useExport } from "./use-export";

// The segment bar sticks 48px below <main>'s top edge (its 24px padding +
// top-6), right under the 49px compact header. Its height depends on the
// chips and the width, so the page measures it into --segment-bar-height and
// a nav click lands a section 16px below the bar.
const SECTION_SCROLL_MARGIN = "scroll-mt-[calc(var(--segment-bar-height,3.5rem)+4rem)]";

export interface ResultsPageProps {
    survey: Survey;
    workspaceId: string;
}

const EMPTY_PLACEMENTS: PlacementRow[] = [];

export function ResultsPage({ survey, workspaceId }: ResultsPageProps) {
    const { rawSearch, onSearchPatch } = useUrlSearch();
    const router = useTimeChipRouter(rawSearch, onSearchPatch);
    // A module constant while loading: a fresh [] every render would rebuild
    // the manifest, and with it kore's whole request, every render. Until
    // the placements load, a placement chip from a link shows its raw id.
    const placements = usePlacements(survey.id).data ?? EMPTY_PLACEMENTS;
    const manifest = useMemo(() => segmentManifest(survey, placements), [survey, placements]);
    const kore = useKoreSearch<RawFilters>({ manifest, router });
    const filters = filtersForWire(kore.rq?.filters);
    const [splitByPlacement, setSplitByPlacement] = useState(false);
    // Held back until kore has read the URL, or the first request would be unfiltered.
    const statsQuery = useStats(survey.id, kore.ready ? { filters, splitByPlacement } : null);
    const statsRetry = useRetry(statsQuery);
    const stats = statsQuery.data;
    const segmentExport = useExport(survey.id);
    const segmentBar = useElementHeight<HTMLFieldSetElement>();
    const selected = presetOf(router.search);
    const toResponses = useMemo(() => segmentSearch(rawSearch, survey, placements), [rawSearch, survey, placements]);
    // Typed as the loose dict: the Responses route declares no search keys.
    const toTextResponses: Record<string, string | string[]> = useMemo(
        () => ({ ...toResponses, hasText: "true" }),
        [toResponses],
    );
    const params = { workspaceId, surveyId: survey.id };

    // The nav names only sections that are on the page. kore's SectionNav
    // attaches its scroll-spy to the ids its items name each time the items
    // change; naming the stats sections before they rendered left it watching
    // nothing, so the highlight never moved. The step entries come from the
    // same list the step anchors render from, labelled by survey order.
    const navItems: SectionNavItem[] = useMemo(() => {
        // Same rule as the render below: an error shows the error, even
        // with an earlier segment's stats still cached.
        if (statsQuery.isError || !stats) return [];
        const steps = [...survey.steps].sort((a, b) => a.position - b.position);
        const shown = new Set(stats.steps.map((summary) => summary.stepId));
        return [
            { id: RESULTS_SECTION.summary, label: "Summary" },
            { id: RESULTS_SECTION.trend, label: "Trend" },
            {
                id: RESULTS_SECTION.steps,
                label: "Steps",
                children: steps.flatMap((step, index) =>
                    shown.has(step.id)
                        ? [{ id: RESULTS_SECTION.step(step.id), label: `${index + 1} · ${stepPrompt(step)}` }]
                        : [],
                ),
            },
            { id: RESULTS_SECTION.timeOfDay, label: "Time of day" },
        ];
    }, [survey.steps, stats, statsQuery.isError]);

    return (
        <SectionPageShell
            backLabel={survey.name}
            backLink={<Link to="/w/$workspaceId/surveys/$surveyId" params={params} />}
            title="Results"
            badges={
                <>
                    <Badge tone={survey.active ? "success" : "neutral"}>{survey.active ? "Active" : "Inactive"}</Badge>
                    {survey.steps.some((step) => step.isMetric) && <Badge tone="accent">Scored</Badge>}
                </>
            }
            actions={
                <>
                    <Button
                        variant="outline"
                        size="sm"
                        // Until kore has read the URL the segment isn't known yet.
                        disabled={!kore.ready || segmentExport.exporting}
                        onClick={() => void segmentExport.run({ filters, orderBy: RESPONSE_DEFAULT_ORDER })}
                    >
                        {segmentExport.exporting ? "Exporting…" : "Export CSV"}
                    </Button>
                    <Link
                        to="/w/$workspaceId/surveys/$surveyId"
                        params={params}
                        className={buttonVariants({ variant: "outline", size: "sm" })}
                    >
                        Edit survey
                    </Link>
                </>
            }
            nav={<SectionNav items={navItems} sticky={false} />}
        >
            {/* display: contents keeps the shell's column layout; the variable
                reaches the sections (and the bar's sticky box is unchanged). */}
            <div
                className="contents"
                style={
                    segmentBar.height === null
                        ? undefined
                        : ({ "--segment-bar-height": `${segmentBar.height}px` } as CSSProperties)
                }
            >
                <SegmentBar
                    ref={segmentBar.ref}
                    barProps={kore.barProps}
                    selected={selected}
                    onPreset={(preset) => onSearchPatch(presetPatch(preset))}
                    responsesLink={
                        <Link
                            to="/w/$workspaceId/surveys/$surveyId/responses"
                            params={params}
                            search={toResponses}
                            // Button height on the top row, as on Responses.
                            className="inline-flex h-8 shrink-0 items-center text-sm text-kore-accent-text hover:underline"
                        >
                            View these responses ›
                        </Link>
                    }
                />
                {statsRetry.failed ? (
                    <PageError {...statsRetry.props} />
                ) : !stats ? (
                    // keepPreviousData holds the old window across a segment change,
                    // so this only shows on the very first load.
                    <PageLoading />
                ) : (
                    <>
                        <SectionCard className={SECTION_SCROLL_MARGIN} id={RESULTS_SECTION.summary} title="Summary">
                            <SummaryTiles stats={stats} survey={survey} allTime={selected === "all"} />
                        </SectionCard>
                        <SectionCard className={SECTION_SCROLL_MARGIN} id={RESULTS_SECTION.trend} title="Trend">
                            <TrendChart
                                stats={stats}
                                splitByPlacement={splitByPlacement}
                                onSplitByPlacementChange={setSplitByPlacement}
                            />
                        </SectionCard>
                        <SectionCard className={SECTION_SCROLL_MARGIN} id={RESULTS_SECTION.steps} title="Steps">
                            {/* Two per row only when the section has room for two cards
                            (a container query: the nav column and sidebar decide the
                            width, not the window). */}
                            <div className="@container">
                                <div className="grid gap-6 @3xl:grid-cols-2">
                                    {stats.steps.map((summary) => (
                                        <div
                                            key={summary.stepId}
                                            id={RESULTS_SECTION.step(summary.stepId)}
                                            className={SECTION_SCROLL_MARGIN}
                                        >
                                            <StepSummaryCard
                                                survey={survey}
                                                summary={summary}
                                                seeAll={
                                                    summary.kind === "text" ? (
                                                        <Link
                                                            to="/w/$workspaceId/surveys/$surveyId/responses"
                                                            params={params}
                                                            search={toTextResponses}
                                                        />
                                                    ) : undefined
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </SectionCard>
                        <SectionCard
                            className={SECTION_SCROLL_MARGIN}
                            id={RESULTS_SECTION.timeOfDay}
                            title="Time of day"
                        >
                            <HeatmapCard heatmap={stats.heatmap} />
                        </SectionCard>
                    </>
                )}
            </div>
        </SectionPageShell>
    );
}
